import React, { KeyboardEvent, useCallback } from 'react';
import { OmitStrict } from '@tanium/coreui-utils';
import styled from '@tanium/react-emotion-9';
import { HEX_3E4B68, HEX_477FBA } from '@tanium/colors';
import { css } from '@tanium/style-system';
import { InternalLinkBase } from './InternalLinkBase';

const SideNavLinkFill = () => 'transparent';
const SideNavLinkFillCurrent = () => HEX_477FBA;
const SideNavLinkFillSelected = () => HEX_3E4B68;

interface Props {
  children?: React.ReactNode;
  to?: string;
  isCurrent: boolean;
  isSelected: boolean;
  onActivate?: (e: React.SyntheticEvent<HTMLSpanElement>) => void;
}

const getBackgroundColor = (isCurrent: boolean, isSelected: boolean) => {
  if (isCurrent) {
    return SideNavLinkFillCurrent;
  }

  if (isSelected) {
    return SideNavLinkFillSelected;
  }

  return SideNavLinkFill;
};

const generalLinkStyles = css({
  // Add extra specificity to override global styles
  '&&': {
    userSelect: 'none',
    display: 'inline-block',
    fontSize: '13px',
    width: '100%',
    cursor: 'pointer',
    textDecoration: 'none',
  },
});

const propDependentStyles = ({
  isCurrent,
  isLink,
  isSelected,
}: {
  isCurrent: boolean;
  isLink: boolean;
  isSelected: boolean;
}) =>
  css({
    // Add extra specificity to override global styles
    '&&': {
      backgroundColor: getBackgroundColor(isCurrent, isSelected),
      cursor: isLink ? 'pointer' : 'default',
    },
  });

interface StyledLinkProps extends Props {
  // to: string;
  isLink: boolean;
}

const StyledNotALink = styled('span', {
  shouldForwardProp: (prop: string) => !['isCurrent', 'isLink', 'isSelected'].includes(prop),
})<OmitStrict<StyledLinkProps, 'to'>>(generalLinkStyles, propDependentStyles);

const StyledLink = StyledNotALink.withComponent(InternalLinkBase);

export const SideNavLink: React.VFC<Props> = ({
  to,
  children,
  isCurrent,
  isSelected,
  onActivate,
  ...passthroughProps
}) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (!onActivate) return;
        onActivate(e);
      }
    },
    [onActivate]
  );

  if (!to) {
    return (
      <StyledNotALink
        role={onActivate ? 'button' : undefined}
        isCurrent={isCurrent}
        isSelected={isSelected}
        isLink={false}
        tabIndex={0}
        onClick={onActivate}
        onKeyDown={handleKeyDown}
        {...passthroughProps}
      >
        {children}
      </StyledNotALink>
    );
  }

  return (
    <StyledLink to={to} isCurrent={isCurrent} isSelected={isSelected} isLink {...passthroughProps}>
      {children}
    </StyledLink>
  );
};
